<template>
  <div class="questions-manager">

    <!-- Header with Test Pack Info -->
    <div class="test-pack-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <b-button
            v-if="showBackButton"
            variant="outline-primary"
            size="sm"
            @click="$emit('back-to-test-packs')"
            class="mr-3"
          >
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1"/>
            Back to Test Packs
          </b-button>
          <div>
            <h4 class="mb-0">{{ testPack.name }}</h4>
            <p class="text-muted mb-0">
              Manage questions for this test pack
              <b-badge variant="outline-info" class="ml-2">{{ questions.length }} questions</b-badge>
            </p>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            variant="outline-success"
            size="sm"
            class="mr-2"
            @click="$refs.importModal.show()"
          >
            <feather-icon icon="UploadIcon" size="16" class="mr-1"/>
            Import Questions
          </b-button>
          <b-button
            variant="primary"
            @click="openCreateQuestionModal"
          >
            <feather-icon icon="PlusIcon" size="16" class="mr-1"/>
            Add Question
          </b-button>
        </div>
      </div>
    </div>

    <!-- Test Pack Stats -->
    <b-row class="mb-4">
      <b-col lg="3" md="6">
        <b-card class="stat-card">
          <div class="stat-content">
            <feather-icon icon="HelpCircleIcon" class="stat-icon text-primary"/>
            <div>
              <h5 class="mb-0">{{ questions.length }}</h5>
              <small class="text-muted">Total Questions</small>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col lg="3" md="6">
        <b-card class="stat-card">
          <div class="stat-content">
            <feather-icon icon="AwardIcon" class="stat-icon text-success"/>
            <div>
              <h5 class="mb-0">{{ totalPoints }}</h5>
              <small class="text-muted">Total Points</small>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col lg="3" md="6">
        <b-card class="stat-card">
          <div class="stat-content">
            <feather-icon icon="ClockIcon" class="stat-icon text-warning"/>
            <div>
              <h5 class="mb-0">{{ estimatedTime }} min</h5>
              <small class="text-muted">Est. Time</small>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col lg="3" md="6">
        <b-card class="stat-card">
          <div class="stat-content">
            <feather-icon icon="BarChartIcon" class="stat-icon text-info"/>
            <div>
              <h5 class="mb-0">{{ avgPointsPerQuestion }}</h5>
              <small class="text-muted">Avg Points</small>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Questions List -->
    <b-card no-body class="questions-card">
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">
            <feather-icon icon="ListIcon" size="16" class="mr-1"/>
            Questions
          </h6>
          <div class="d-flex align-items-center">
            <b-form-group class="mb-0 mr-3">
              <v-select
                v-model="filterType"
                :options="questionTypeOptions"
                placeholder="Filter by type..."
                :clearable="true"
                style="width: 200px;"
              />
            </b-form-group>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="loadQuestions"
              :disabled="isLoading"
            >
              <feather-icon icon="RefreshCwIcon" :class="{ 'spinner': isLoading }" size="14"/>
            </b-button>
          </div>
        </div>
      </b-card-header>

      <div class="questions-list" v-if="filteredQuestions.length > 0">
        <div
          v-for="(question, index) in filteredQuestions"
          :key="question.id"
          class="question-item"
          :class="{ 'question-dragging': draggedQuestion === question.id }"
          draggable="true"
          @dragstart="handleDragStart(question, $event)"
          @dragover="handleDragOver($event)"
          @drop="handleDrop(question, $event)"
        >
          <div class="question-header">
            <div class="d-flex align-items-start justify-content-between">
              <div class="question-meta d-flex align-items-center">
                <div class="drag-handle mr-2">
                  <feather-icon icon="MenuIcon" size="14" class="text-muted"/>
                </div>
                <div class="question-number mr-3">
                  <b-badge variant="outline-primary">#{{ index + 1 }}</b-badge>
                </div>
                <div class="question-type mr-3">
                  <b-badge :variant="getQuestionTypeVariant(question.question_type)">
                    {{ getQuestionTypeLabel(question.question_type) }}
                  </b-badge>
                </div>
                <div class="question-difficulty mr-3" v-if="question.difficulty_level">
                  <b-badge :variant="getDifficultyVariant(question.difficulty_level)">
                    {{ question.difficulty_level }}
                  </b-badge>
                </div>
                <div class="question-points">
                  <small class="text-muted">
                    <feather-icon icon="AwardIcon" size="12" class="mr-1"/>
                    {{ question.points || 0 }} pts
                  </small>
                </div>
              </div>

              <b-dropdown variant="link" no-caret size="sm">
                <template #button-content>
                  <feather-icon icon="MoreVerticalIcon" size="16"/>
                </template>

                <b-dropdown-item @click="editQuestion(question)">
                  <feather-icon icon="EditIcon" size="14" class="mr-2"/>
                  Edit Question
                </b-dropdown-item>

                <b-dropdown-item @click="duplicateQuestion(question)">
                  <feather-icon icon="CopyIcon" size="14" class="mr-2"/>
                  Duplicate
                </b-dropdown-item>

                <b-dropdown-divider></b-dropdown-divider>

                <b-dropdown-item @click="deleteQuestion(question)" variant="danger">
                  <feather-icon icon="TrashIcon" size="14" class="mr-2"/>
                  Delete
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <div class="question-content">
            <div class="question-text mb-2">
              <h6 class="mb-1">{{ question.question }}</h6>
              <p v-if="question.explanation" class="question-explanation text-muted mb-0">
                {{ question.explanation }}
              </p>
            </div>

            <!-- Question Type Specific Content -->
            <div v-if="question.question_type === 'multiple_choice' && question.options" class="question-options">
              <div class="d-flex flex-wrap">
                <div
                  v-for="(option, optionIndex) in question.options"
                  :key="optionIndex"
                  class="option-item mr-3 mb-2"
                >
                  <b-badge
                    :variant="option === question.correct_answer ? 'success' : 'light'"
                    class="option-badge"
                  >
                    {{ String.fromCharCode(65 + optionIndex) }}. {{ option }}
                    <feather-icon
                      v-if="option === question.correct_answer"
                      icon="CheckIcon"
                      size="12"
                      class="ml-1"
                    />
                  </b-badge>
                </div>
              </div>
            </div>

            <div v-else-if="question.question_type === 'true_false'" class="question-answer">
              <b-badge
                :variant="question.correct_answer === 'true' ? 'success' : 'danger'"
                class="answer-badge"
              >
                Correct Answer: {{ question.correct_answer === 'true' ? 'True' : 'False' }}
              </b-badge>
            </div>

            <div v-else-if="question.question_type === 'essay'" class="essay-info">
              <div class="d-flex align-items-center flex-wrap">
                <small class="text-muted mr-3" v-if="question.min_words">
                  Min words: {{ question.min_words }}
                </small>
                <small class="text-muted mr-3" v-if="question.max_words">
                  Max words: {{ question.max_words }}
                </small>
                <small class="text-muted" v-if="question.essay_prompt">
                  Prompt: {{ question.essay_prompt | truncate(100) }}
                </small>
              </div>
            </div>

            <!-- Media Files -->
            <div v-if="question.question_image || question.question_audio" class="question-media mt-2">
              <div class="d-flex align-items-center">
                <b-badge v-if="question.question_image" variant="light-info" class="mr-2">
                  <feather-icon icon="ImageIcon" size="12" class="mr-1"/>
                  Image
                </b-badge>
                <b-badge v-if="question.question_audio" variant="light-warning">
                  <feather-icon icon="VolumeIcon" size="12" class="mr-1"/>
                  Audio
                </b-badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!isLoading" class="empty-state">
        <div class="text-center p-5">
          <feather-icon icon="HelpCircleIcon" size="48" class="text-muted mb-3"/>
          <h5 class="text-muted">No questions found</h5>
          <p class="text-muted">Start by adding your first question to this test pack.</p>
          <b-button variant="primary" @click="openCreateQuestionModal">
            <feather-icon icon="PlusIcon" size="16" class="mr-1"/>
            Add First Question
          </b-button>
        </div>
      </div>

      <div v-else class="loading-state">
        <div class="text-center p-4">
          <b-spinner variant="primary" class="mb-2"></b-spinner>
          <p class="text-muted mb-0">Loading questions...</p>
        </div>
      </div>
    </b-card>

    <!-- Question Modal -->
    <QuestionModal
      :visible="showQuestionModal"
      :question="editingQuestion"
      :test-pack="testPack"
      @save="onQuestionSaved"
      @close="closeQuestionModal"
    />

  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardHeader,
  BRow,
  BCol,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BFormGroup,
  BSpinner
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import QuestionModal from './QuestionModal.vue'
import TestManagementApi from '@/api/testManagement'

export default {
  name: 'QuestionsManager',
  components: {
    BButton,
    BCard,
    BCardHeader,
    BRow,
    BCol,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BFormGroup,
    BSpinner,
    vSelect,
    QuestionModal
  },

  props: {
    testPack: {
      type: Object,
      required: true
    },
    showBackButton: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isLoading: false,
      questions: [],
      filterType: null,
      showQuestionModal: false,
      editingQuestion: null,
      draggedQuestion: null,

      questionTypeOptions: [
        { label: 'Multiple Choice', value: 'multiple_choice' },
        { label: 'True/False', value: 'true_false' },
        { label: 'Essay', value: 'essay' },
        { label: 'Short Answer', value: 'short_answer' },
        { label: 'Fill in Blank', value: 'fill_blank' }
      ]
    }
  },

  computed: {
    filteredQuestions() {
      if (!this.filterType) {
        return this.questions
      }
      return this.questions.filter(q => q.question_type === this.filterType)
    },

    totalPoints() {
      return this.questions.reduce((sum, q) => sum + (q.points || 0), 0)
    },

    estimatedTime() {
      // Estimate 2 minutes per question
      return Math.round(this.questions.length * 2)
    },

    avgPointsPerQuestion() {
      if (this.questions.length === 0) return 0
      return Math.round((this.totalPoints / this.questions.length) * 10) / 10
    }
  },

  filters: {
    truncate(value, length = 100) {
      if (!value) return ''
      return value.length > length ? value.substring(0, length) + '...' : value
    }
  },

  mounted() {
    this.loadQuestions()
  },

  methods: {
    async loadQuestions() {
      this.isLoading = true
      try {
        const api = new TestManagementApi()
        const response = await api.getTestPackQuestions(this.testPack.id)
        this.questions = response.data.questions || []
      } catch (error) {
        console.error('Error loading questions:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to load questions',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    openCreateQuestionModal() {
      this.editingQuestion = null
      this.showQuestionModal = true
    },

    closeQuestionModal() {
      this.showQuestionModal = false
      this.editingQuestion = null
    },

    editQuestion(question) {
      this.editingQuestion = question
      this.showQuestionModal = true
    },

    async duplicateQuestion(question) {
      try {
        const duplicateData = {
          ...question,
          question: question.question + ' (Copy)',
          test_pack_id: this.testPack.id
        }
        delete duplicateData.id
        delete duplicateData.created_at
        delete duplicateData.updated_at

        const api = new TestManagementApi()
        await api.createQuestion(duplicateData)

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: 'Question duplicated successfully',
            variant: 'success',
          },
        })

        await this.loadQuestions()
        this.$emit('question-created')
      } catch (error) {
        console.error('Error duplicating question:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to duplicate question',
            variant: 'danger',
          },
        })
      }
    },

    async deleteQuestion(question) {
      try {
        const result = await this.$bvModal.msgBoxConfirm(
          `Are you sure you want to delete this question? This action cannot be undone.`,
          {
            title: 'Confirm Deletion',
            size: 'md',
            okVariant: 'danger',
            okTitle: 'Delete',
            cancelTitle: 'Cancel'
          }
        )

        if (result) {
          const api = new TestManagementApi()
          await api.deleteQuestion(question.id)

          this.$toast({
            component: 'ToastificationContent',
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              text: 'Question deleted successfully',
              variant: 'success',
            },
          })

          await this.loadQuestions()
          this.$emit('question-deleted')
        }
      } catch (error) {
        console.error('Error deleting question:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: 'Failed to delete question',
            variant: 'danger',
          },
        })
      }
    },

    async onQuestionSaved() {
      this.closeQuestionModal()
      await this.loadQuestions()
      this.$emit('question-updated')
    },

    // Drag and Drop handlers
    handleDragStart(question, event) {
      this.draggedQuestion = question.id
      event.dataTransfer.setData('text/plain', question.id)
    },

    handleDragOver(event) {
      event.preventDefault()
    },

    async handleDrop(targetQuestion, event) {
      event.preventDefault()
      const draggedQuestionId = parseInt(event.dataTransfer.getData('text/plain'))

      if (draggedQuestionId !== targetQuestion.id) {
        // Reorder questions logic here
        // You can implement API call to update question order
        console.log('Reordering questions:', draggedQuestionId, 'to', targetQuestion.id)
      }

      this.draggedQuestion = null
    },

    // Helper methods
    getQuestionTypeLabel(type) {
      const typeLabels = {
        'multiple_choice': 'Multiple Choice',
        'true_false': 'True/False',
        'essay': 'Essay',
        'short_answer': 'Short Answer',
        'fill_blank': 'Fill in Blank'
      }
      return typeLabels[type] || type
    },

    getQuestionTypeVariant(type) {
      const variants = {
        'multiple_choice': 'light-primary',
        'true_false': 'light-success',
        'essay': 'light-warning',
        'short_answer': 'light-info',
        'fill_blank': 'light-secondary'
      }
      return variants[type] || 'light-secondary'
    },

    getDifficultyVariant(difficulty) {
      const variants = {
        'easy': 'light-success',
        'medium': 'light-warning',
        'hard': 'light-danger'
      }
      return variants[difficulty] || 'light-secondary'
    }
  }
}
</script>

<style lang="scss" scoped>
.questions-manager {
  .test-pack-header {
    background: linear-gradient(118deg, #7367F0, rgba(115, 103, 240, 0.7));
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    color: white;

    h4 {
      color: white !important;
    }
  }

  .stat-card {
    border: none;
    box-shadow: 0 2px 15px 0 rgba(0,0,0,0.1);

    .stat-content {
      display: flex;
      align-items: center;

      .stat-icon {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: rgba(115, 103, 240, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;
      }

      h5 {
        font-weight: 700;
        margin-bottom: 0;
      }
    }
  }

  .questions-card {
    box-shadow: 0 4px 25px 0 rgba(0,0,0,0.1);

    .questions-list {
      max-height: 600px;
      overflow-y: auto;
    }

    .question-item {
      border-bottom: 1px solid #f0f0f0;
      padding: 1rem;
      transition: all 0.3s ease;
      cursor: move;

      &:hover {
        background: #f8f9fa;
      }

      &.question-dragging {
        opacity: 0.5;
      }

      &:last-child {
        border-bottom: none;
      }

      .question-header {
        margin-bottom: 0.75rem;
      }

      .question-meta {
        .drag-handle {
          cursor: move;
        }

        .question-number .badge {
          font-size: 0.75rem;
        }

        .question-type .badge {
          font-size: 0.7rem;
        }

        .question-difficulty .badge {
          font-size: 0.7rem;
        }
      }

      .question-content {
        .question-text h6 {
          color: #2c2c2c;
          font-weight: 600;
        }

        .question-explanation {
          font-size: 0.9rem;
        }

        .question-options {
          .option-item {
            .option-badge {
              font-size: 0.8rem;
              padding: 0.375rem 0.75rem;
            }
          }
        }

        .answer-badge {
          font-size: 0.8rem;
          padding: 0.375rem 0.75rem;
        }

        .essay-info small {
          font-size: 0.8rem;
        }

        .question-media .badge {
          font-size: 0.7rem;
        }
      }
    }

    .empty-state,
    .loading-state {
      border-top: 1px solid #f0f0f0;
    }
  }
}

// Spinner animation
.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>